/**
 * 1.8.0: the Rights block of the watch folder and live source dialogs (plan section 2b): the model's rights fields
 * (Status, Rights holder, Source, Reference, Restrictions, Credit) stored as ordinary defaults, and Available from
 * and until as none, a fixed value, or relative to arrival (a watch folder) or to the recording's start (a live
 * source): `rightsDates`. Shown only while rights management is on; saving changed rights defaults needs
 * rights.manage (the server answers 403 RIGHTS_MANAGE_REQUIRED).
 */
import { useState } from 'react';
import { rightsFields, type FieldDefinition, type MetadataSchemaDefinition, type RightsDates } from '@prism/contracts';
import { toLocalInput } from '../../components/MetadataFields';
import type { RightsRelativeTo } from './rightsDefaultsText';

function ValueInput({ field, value, onChange, testId }: { field: FieldDefinition; value: unknown; onChange: (v: unknown) => void; testId?: string }) {
  if (field.type === 'vocabulary') {
    return (
      <select className="select" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)} data-testid={testId}>
        <option value="">—</option>
        {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.role === 'rights_restrictions') {
    return <textarea className="textarea" rows={2} maxLength={2000} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)} data-testid={testId} />;
  }
  return <input className="input" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)} data-testid={testId} />;
}

type DateMode = 'none' | 'fixed' | 'relative';

export function RightsDefaultsBlock({ schema, defaults, onDefaults, dates, onDates, relativeTo, testId }: {
  schema: MetadataSchemaDefinition | undefined;
  defaults: Record<string, unknown>;
  onDefaults: (next: Record<string, unknown>) => void;
  dates: RightsDates | undefined;
  onDates: (next: RightsDates | undefined) => void;
  relativeTo: RightsRelativeTo;
  testId: string;
}) {
  const f = schema ? rightsFields(schema) : {};
  const initial = (rel: boolean, fixed: FieldDefinition | undefined): DateMode => (rel ? 'relative' : fixed && defaults[fixed.id] ? 'fixed' : 'none');
  const [fromMode, setFromMode] = useState<DateMode>(() => initial(dates?.fromAfterHours !== undefined, f.rights_from));
  const [untilMode, setUntilMode] = useState<DateMode>(() => initial(dates?.untilAfterDays !== undefined, f.rights_until));
  if (!schema) return null;
  const setValue = (field: FieldDefinition | undefined, v: unknown) => {
    if (!field) return;
    const next = { ...defaults };
    if (v === undefined) delete next[field.id]; else next[field.id] = v;
    onDefaults(next);
  };
  const plain = [f.rights_status, f.rights_holder, f.rights_source, f.rights_reference, f.rights_restrictions, f.rights_credit].filter((x): x is FieldDefinition => Boolean(x));
  const after = relativeTo === 'arrival' ? 'after arrival' : 'after the recording starts';
  const setDates = (patch: Partial<RightsDates>, drop: keyof RightsDates | null) => {
    const next: RightsDates = { ...(dates ?? {}), ...patch };
    if (drop) delete next[drop];
    onDates(Object.keys(next).length ? next : undefined);
  };
  const chooseFrom = (m: DateMode) => {
    setFromMode(m);
    if (m !== 'fixed') setValue(f.rights_from, undefined);
    if (m === 'relative') setDates({ fromAfterHours: dates?.fromAfterHours ?? 0 }, null); else setDates({}, 'fromAfterHours');
  };
  const chooseUntil = (m: DateMode) => {
    setUntilMode(m);

    if (m !== 'fixed') setValue(f.rights_until, undefined);
    if (m === 'relative') setDates({ untilAfterDays: dates?.untilAfterDays ?? 30 }, null); else setDates({}, 'untilAfterDays');
  };
  if (!plain.length && !f.rights_from && !f.rights_until) {
    return (
      <div className="field rights-defaults" data-testid={testId}><label>Rights</label>
        <span className="help">The metadata model has no rights fields yet: an administrator adds them in Administration → Rights.</span>
      </div>
    );
  }
  return (
    <div className="field rights-defaults" data-testid={testId}>
      <label>Rights</label>
      <span className="help">Given to every asset that arrives {relativeTo === 'arrival' ? 'through this folder' : 'from this source'}. {relativeTo === 'arrival' ? 'A sidecar can still set its own embargo or restriction.' : ''}</span>
      <div className="rights-defaults-grid">
        {plain.map((field) => (
          <div className="field" key={field.id} style={field.role === 'rights_restrictions' ? { gridColumn: '1 / -1' } : undefined}>
            <label style={{ fontSize: 12 }}>{field.label}</label>
            <ValueInput field={field} value={defaults[field.id]} onChange={(v) => setValue(field, v)} testId={`${testId}-${field.role}`} />
          </div>
        ))}
        {f.rights_from && (
          <div className="field">
            <label style={{ fontSize: 12 }}>{f.rights_from.label}</label>
            <select className="select" value={fromMode} onChange={(e) => chooseFrom(e.target.value as DateMode)} data-testid={`${testId}-from-mode`}>
              <option value="none">None</option>
              <option value="fixed">A date and time</option>
              <option value="relative">Hours {after}</option>
            </select>
            {fromMode === 'fixed' && (
              <input className="input" type="datetime-local" value={toLocalInput(String(defaults[f.rights_from.id] ?? ''))}
                onChange={(e) => setValue(f.rights_from, e.target.value ? new Date(e.target.value).toISOString() : undefined)} />
            )}
            {fromMode === 'relative' && (
              <span className="rights-relative">
                <input className="input" type="number" min={0} max={8760} value={dates?.fromAfterHours ?? 0} style={{ maxWidth: 90 }}
                  onChange={(e) => setDates({ fromAfterHours: Math.max(0, Math.min(8760, Math.round(Number(e.target.value) || 0))) }, null)} data-testid={`${testId}-from-hours`} />
                <span className="muted">hours {after}</span>
              </span>
            )}
          </div>
        )}
        {f.rights_until && (
          <div className="field">
            <label style={{ fontSize: 12 }}>{f.rights_until.label}</label>
            <select className="select" value={untilMode} onChange={(e) => chooseUntil(e.target.value as DateMode)} data-testid={`${testId}-until-mode`}>
              <option value="none">None</option>
              <option value="fixed">A date</option>
              <option value="relative">Days {after}</option>
            </select>
            {untilMode === 'fixed' && (
              <input className="input" type="date" value={String(defaults[f.rights_until.id] ?? '')} onChange={(e) => setValue(f.rights_until, e.target.value || undefined)} />
            )}
            {untilMode === 'relative' && (
              <span className="rights-relative">
                <input className="input" type="number" min={0} max={3650} value={dates?.untilAfterDays ?? 30} style={{ maxWidth: 90 }}
                  onChange={(e) => setDates({ untilAfterDays: Math.max(0, Math.min(3650, Math.round(Number(e.target.value) || 0))) }, null)} data-testid={`${testId}-until-days`} />
                <span className="muted">days {after}</span>
              </span>
            )}
          </div>
        )}
      </div>
      <span className="help">Relative dates are worked out in the rights time zone when {relativeTo === 'arrival' ? 'the file arrives' : 'the recording starts'}, so later changes here don’t move existing assets.</span>
    </div>
  );
}
